/**
 * Runtime feature flags.
 *
 * Runtime rather than build-time on purpose. A VITE_ env flag would need its own
 * build and its own deploy target, so what we tested would not be the artefact
 * that ships. This is one build, one deploy: the flag is off for everyone,
 * including every current student, until someone adds ?ui=next to the URL. That
 * makes the new work reachable on real production data from a real phone while
 * being invisible to people mid-study.
 *
 * The value sticks in localStorage so the query string is only needed once, and
 * ?ui=off clears it.
 */

const STORE_KEY = "bht:flags";

export type FlagName = "homeNext";

const DEFAULTS: Record<FlagName, boolean> = {
  // The redesigned home screen, default since 2026-09-05 after an outside UX
  // review and the changes it prompted: grouped shortcut grids instead of one
  // homogeneous list, a smaller hero under a RECOMMENDED label, and consistent
  // subject-card metrics. ?ui=off still returns a student to the old Home, which
  // is why Home.tsx stays in the tree rather than being deleted with the flag.
  homeNext: true,
};

function read(): Partial<Record<FlagName, boolean>> {
  // The localStorage ACCESS must be inside the try. When a browser blocks site
  // data, `localStorage` is a getter that throws SecurityError; `typeof` only
  // suppresses ReferenceError for an unresolvable name, and this one resolves,
  // so a `typeof` guard is itself the throw site. flag() is read on every render
  // of every route, so getting this wrong took down the whole app, signed out
  // and flag off included. theme.ts and session.ts already do it this way.
  try {
    const parsed = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}");
    // JSON.parse("null") returns null without throwing, and the store is a
    // key any extension or earlier version can write.
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write(next: Partial<Record<FlagName, boolean>>) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  } catch {
    /* private mode, or storage full: the flag simply does not persist */
  }
}

/**
 * Read ?ui= once per load and persist it. Called before the app renders so the
 * first paint already reflects the flag rather than flipping after hydration.
 */
export function initFlagsFromUrl() {
  // Runs at module scope before React mounts, so an exception here is a blank
  // page with no error boundary above it.
  if (typeof window === "undefined") return;
  let ui: string | null = null;
  try {
    ui = new URLSearchParams(window.location.search).get("ui");
  } catch {
    return;
  }
  if (!ui) return;
  const flags = read();
  if (ui === "next") flags.homeNext = true;
  if (ui === "off") flags.homeNext = false;
  write(flags);
}

export function flag(name: FlagName): boolean {
  return read()[name] ?? DEFAULTS[name];
}
